/**
 * Data loading utilities
 * Loads static JSON data files for the optimizer
 */

import type { Zip3ReferenceMap, ZoneMatrix } from '../types';

// URLs for data files (use Vite's base URL for subdirectory deployments)
const BASE = import.meta.env.BASE_URL;
const ZIP3_BOUNDARIES_URL = `${BASE}data/zip3-boundaries.json`;
const ZIP3_REFERENCE_URL = `${BASE}data/zip3-reference.json`;
const ZONE_MATRIX_URL = `${BASE}data/zone-matrix.json`;

// Cache for loaded data
let zip3BoundariesCache: any = null;
let zip3ReferenceCache: Zip3ReferenceMap | null = null;
let zoneMatrixCache: ZoneMatrix | null = null;
const carrierMatrixCache: Map<string, ZoneMatrix> = new Map();

/**
 * Load ZIP3 boundaries GeoJSON (for map rendering)
 */
export async function loadZip3Boundaries(): Promise<any> {
  if (zip3BoundariesCache) {
    return zip3BoundariesCache;
  }

  console.log('Loading ZIP3 boundaries...');
  const response = await fetch(ZIP3_BOUNDARIES_URL);
  
  if (!response.ok) {
    throw new Error(`Failed to load ZIP3 boundaries: ${response.statusText}`);
  }
  
  zip3BoundariesCache = await response.json();
  console.log(`Loaded ${zip3BoundariesCache.features?.length || 0} ZIP3 boundary features`);
  
  return zip3BoundariesCache;
}

/**
 * Load ZIP3 reference data (centroids, populations, states)
 */
export async function loadZip3Reference(): Promise<Zip3ReferenceMap> {
  if (zip3ReferenceCache) {
    return zip3ReferenceCache;
  }

  console.log('Loading ZIP3 reference data...');
  const response = await fetch(ZIP3_REFERENCE_URL);
  
  if (!response.ok) {
    throw new Error(`Failed to load ZIP3 reference: ${response.statusText}`);
  }
  
  zip3ReferenceCache = await response.json();
  console.log(`Loaded ${Object.keys(zip3ReferenceCache!).length} ZIP3 reference entries`);
  
  return zip3ReferenceCache!;
}

/**
 * Load zone matrix (transit times)
 * @param carrier Optional carrier code ("ups", "usps") - if not provided, loads consolidated matrix
 */
export async function loadZoneMatrix(carrier?: string): Promise<ZoneMatrix> {
  // Check cache
  if (carrier) {
    const cached = carrierMatrixCache.get(carrier);
    if (cached) return cached;
  } else {
    if (zoneMatrixCache) return zoneMatrixCache;
  }

  // Build URL
  const url = carrier 
    ? `${BASE}data/zone-matrix-${carrier.toLowerCase()}.json`
    : ZONE_MATRIX_URL;

  console.log(`Loading zone matrix${carrier ? ` for ${carrier.toUpperCase()}` : ' (consolidated)'}...`);
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to load zone matrix${carrier ? ` for ${carrier}` : ''}: ${response.statusText}`);
  }
  
  const matrix = await response.json();
  
  // Add carrier metadata if loading a carrier-specific file
  if (carrier) {
    matrix.carrier = carrier.toUpperCase();
  }
  
  console.log(`Loaded ${matrix.origins.length}x${matrix.destinations.length} zone matrix${carrier ? ` for ${carrier.toUpperCase()}` : ''}`);
  
  // Cache it
  if (carrier) {
    carrierMatrixCache.set(carrier, matrix);
  } else {
    zoneMatrixCache = matrix;
  }
  
  return matrix;
}

/**
 * Merge multiple carrier matrices into a single matrix
 * For each origin-destination pair, takes the minimum transit time across all carriers
 * Also tracks which carrier provides the best time for each route
 */
function mergeMatrices(matrices: ZoneMatrix[]): ZoneMatrix {
  if (matrices.length === 0) {
    throw new Error('Cannot merge empty matrix array');
  }

  if (matrices.length === 1) {
    return matrices[0];
  }

  const base = matrices[0];
  
  // Validate that all matrices have identical dimensions
  for (let m = 1; m < matrices.length; m++) {
    const matrix = matrices[m];
    const carrierLabel = matrix.carrier || `matrix ${m}`;
    
    // Check origins array length
    if (matrix.origins.length !== base.origins.length) {
      throw new Error(
        `Matrix dimension mismatch: ${carrierLabel} has ${matrix.origins.length} origins, ` +
        `but ${base.carrier || 'base matrix'} has ${base.origins.length}`
      );
    }
    
    // Check destinations array length
    if (matrix.destinations.length !== base.destinations.length) {
      throw new Error(
        `Matrix dimension mismatch: ${carrierLabel} has ${matrix.destinations.length} destinations, ` +
        `but ${base.carrier || 'base matrix'} has ${base.destinations.length}`
      );
    }
    
    // Check matrix data dimensions
    if (matrix.matrix.length !== base.origins.length) {
      throw new Error(
        `Matrix data mismatch: ${carrierLabel} has ${matrix.matrix.length} rows, ` +
        `expected ${base.origins.length}`
      );
    }
    
    // Verify origins and destinations are in the same order
    for (let i = 0; i < base.origins.length; i++) {
      if (matrix.origins[i] !== base.origins[i]) {
        throw new Error(
          `Origin mismatch at index ${i}: ${carrierLabel} has "${matrix.origins[i]}", ` +
          `but base has "${base.origins[i]}". Origins must be in identical order.`
        );
      }
    }
    
    for (let i = 0; i < base.destinations.length; i++) {
      if (matrix.destinations[i] !== base.destinations[i]) {
        throw new Error(
          `Destination mismatch at index ${i}: ${carrierLabel} has "${matrix.destinations[i]}", ` +
          `but base has "${base.destinations[i]}". Destinations must be in identical order.`
        );
      }
    }
  }

  const mergedMatrix: number[][] = [];
  const carrierMap: string[][] = []; // Track which carrier wins each route

  // For each origin
  for (let i = 0; i < base.origins.length; i++) {
    const row: number[] = [];
    const carrierRow: string[] = [];
    
    // For each destination
    for (let j = 0; j < base.destinations.length; j++) {
      // Find minimum transit time across all carriers
      let minDays = 99;
      let winningCarrier = '';
      
      for (const matrix of matrices) {
        // Validate row exists
        if (!matrix.matrix[i]) {
          throw new Error(
            `Missing row ${i} in ${matrix.carrier || 'matrix'} (origin: ${base.origins[i]})`
          );
        }
        
        const days = matrix.matrix[i][j];
        
        // Validate value exists and is a valid number
        if (days === undefined || days === null) {
          throw new Error(
            `Missing data at [${i}][${j}] in ${matrix.carrier || 'matrix'} ` +
            `(origin: ${base.origins[i]}, destination: ${base.destinations[j]})`
          );
        }
        
        if (typeof days !== 'number' || isNaN(days)) {
          throw new Error(
            `Invalid transit time at [${i}][${j}] in ${matrix.carrier || 'matrix'}: ${days} ` +
            `(origin: ${base.origins[i]}, destination: ${base.destinations[j]})`
          );
        }
        
        if (days < minDays) {
          minDays = days;
          winningCarrier = matrix.carrier || '';
        }
      }
      
      row.push(minDays);
      carrierRow.push(winningCarrier);
    }
    
    mergedMatrix.push(row);
    carrierMap.push(carrierRow);
  }

  return {
    origins: base.origins,
    destinations: base.destinations,
    matrix: mergedMatrix,
    carrierMap, // Add carrier attribution map
    // Don't set carrier since this is merged
  };
}

/**
 * Load and merge multiple carrier matrices
 */
export async function loadZoneMatrices(carriers: string[]): Promise<ZoneMatrix> {
  if (carriers.length === 0) {
    return loadZoneMatrix(); // Return consolidated if no carriers specified
  }

  if (carriers.length === 1) {
    return loadZoneMatrix(carriers[0]);
  }

  console.log(`Loading and merging matrices for carriers: ${carriers.join(', ')}`);
  const matrices = await Promise.all(
    carriers.map(carrier => loadZoneMatrix(carrier))
  );

  return mergeMatrices(matrices);
}

/**
 * Load all data files in parallel
 */
export async function loadAllData(): Promise<{
  zip3Boundaries: any;
  zip3Reference: Zip3ReferenceMap;
  zoneMatrix: ZoneMatrix;
}> {
  const [zip3Boundaries, zip3Reference, zoneMatrix] = await Promise.all([
    loadZip3Boundaries(),
    loadZip3Reference(),
    loadZoneMatrix(),
  ]);

  return { zip3Boundaries, zip3Reference, zoneMatrix };
}

/**
 * Check if all data is loaded
 */
export function isDataLoaded(): boolean {
  return !!(zip3BoundariesCache && zip3ReferenceCache && zoneMatrixCache);
}

/**
 * Clear cached data (for testing/reload)
 */
export function clearCache(): void {
  zip3BoundariesCache = null;
  zip3ReferenceCache = null;
  zoneMatrixCache = null;
  carrierMatrixCache.clear();
}

